<script lang="ts">
  import Button from '$components/elements/Button.svelte';
  import BasicLogo from '$components/logo/BasicLogo.svelte';
  import MediaQuery from '$components/utils/MediaQuery.svelte';
  import Icon from '$components/elements/Icon.svelte';

  export let showModal = false;

  export let closeOnEsc = true;
  export let closeOnOuterClick = true;

  export let width = '560px';
  export let closable = true;

  export let closeButtonText: string | undefined = undefined;

  export let overlayOnMobile = false;

  export let floatingCloseButtonColor: undefined | string = undefined;

  export let backgroundColor = '#fff';
  export let border = '1.5px solid rgba($color-medium-gray, 0.8)';

  export let onClose: () => void = () => {
    showModal = false;
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (closeOnEsc && closable && event.key === 'Escape') {
      event.preventDefault();
      onClose();
      close();
    }
  };
</script>

<svelte:window on:keydown={handleKeydown} />

{#if showModal}
  <div
    class="modal"
    class:modal--overlay-on-mobile={overlayOnMobile}
    style={`
      --width: ${width};
      --background-color: ${backgroundColor};
      --border: ${border};
    `}
    on:close={onClose}
    on:click|self={closeOnOuterClick && closable ? onClose : undefined}
    on:keypress|self={closeOnOuterClick && closable ? onClose : undefined}
  >
    <MediaQuery query="screen and (max-width: 720px)" let:matches>
      {#if matches && !overlayOnMobile}
        <div class="modal__mobile-header">
          <div class="modal__mobile-header__logo">
            <BasicLogo />
          </div>
          {#if closable}
            <div
              class="modal__mobile-header__close-button"
              on:click={onClose}
              on:keypress={onClose}
            />
          {/if}
        </div>
      {/if}
      <div on:click|stopPropagation class="modal__content" on:keypress|stopPropagation>
        {#if floatingCloseButtonColor && closable}
          <div
            class="modal__content__floating-close-button"
            on:click={onClose}
            on:keypress={onClose}
          >
            <Icon
              icon="cross"
              color={floatingCloseButtonColor}
              size={48}
              casing="56px"
              cursor="pointer"
            />
          </div>
        {/if}
        <div class="modal__content__header">
          <slot name="header" />
        </div>
        <slot />
        {#if closable && closeButtonText}
          {#if matches}
            <Button width="100%" on:click={onClose}>{closeButtonText}</Button>
          {:else}
            <Button on:click={onClose}>{closeButtonText}</Button>
          {/if}
        {/if}
      </div>
    </MediaQuery>
  </div>
{/if}

<style lang="scss">
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba($color-black, 0.5);

    z-index: 101;

    display: grid;
    justify-content: center;
    align-items: center;
    overflow: scroll;
    padding: 100px 0;
    min-height: 0;

    &__content {
      border: var(--border);
      border-radius: 13px;

      background-color: var(--background-color);
      width: var(--width);
      max-width: 90vw;

      display: flex;
      flex-direction: column;

      align-items: center;
      position: relative;
      overflow: visible;

      &__header {
        width: 100%;
      }
      &__floating-close-button {
        position: absolute;
        bottom: 100%;
        right: 0;
      }
    }
  }

  @include mobile {
    .modal {
      border: none;
      position: static;

      background-color: $color-white;
      padding: 0;

      &__mobile-header {
        width: 100%;
        height: 56px;
        position: fixed;
        top: 0;

        background-color: $color-white;

        display: flex;
        align-items: center;
        justify-content: space-between;
        z-index: 102;

        &__logo {
          display: inline-block;
          padding-left: 17px;
        }

        &__close-button {
          position: relative;
          margin-left: auto;
          margin-right: 22px;

          height: 24px;
          width: 24px;

          cursor: pointer;
          &::before,
          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 15px;
            height: 1.5px;
            background-color: $color-dark-gray;

            border-radius: 100px;
          }

          &::before {
            transform: translate(-50%, -50%) rotate(45deg);
          }
          &::after {
            transform: translate(-50%, -50%) rotate(-45deg);
          }
        }
      }

      &__content {
        border: none;
        border-radius: 0;

        margin-top: 56px;

        position: static;
        max-width: 100vw;
      }
      &--overlay-on-mobile {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba($color-black, 0.5);

        z-index: 101;

        display: flex;
        justify-content: center;
        align-items: center;
        .modal {
          &__content {
            border-radius: 13px;
            width: 90vw;
          }
        }
      }
    }
  }
</style>
